<template>
    <transition
        @before-enter="appearAnimationBefore"
        @enter="appearAnimationEnter"
        @leave="appearAnimationLeave"
    >
        <div
            ref="modal"
            tabindex="0"
            v-show="active"
            :class="['z-modal', classObject]"
        >
            <div
                class="z-modal__overlay"
                @click="closeModal({ id: id, element: 'overlay'})"
            ></div>
            <div
                v-if="active"
                ref="container"
                class="z-modal__container"
            >
                <button
                    v-if="!hasSlotData"
                    role="button"
                    @click="closeModal({id: id, element: 'button'})"
                    :title="text.close"
                    class="z-modal__close"
                    ref="close-btn"
                >
                    <z-icon
                        name="tools/close-small"
                        width="32"
                        height="32"
                    />
                </button>

                <component
                    v-if="!hasSlotData"
                    class="z-modal__component"
                    :is="currentComponent"
                    :data="currentSlide"
                    :offset-top="offsetTop"
                    :offset-bottom="offsetBottom"
                    :download="download"
                    :theme="theme"
                    ref="content"
                />

                <modal-html v-if="hasSlotData" :has-slot-data="hasSlotData">
                    <button
                        role="button"
                        @click="closeModal({id: id, element: 'button'})"
                        :title="text.close"
                        class="z-modal__close z-modal__close--html"
                        ref="close-btn"
                    >
                        <z-icon
                            name="tools/close-small"
                            width="32"
                            height="32"
                        />
                    </button>
                    <slot/>
                </modal-html>

                <div class="z-modal__bottom">
                    <z-button v-if="download" kind="bordered" border-radius size="s">
                        <z-link
                            :href="currentSlide.type === 'video' ? currentSlide.video.src : currentSlide.link"
                            :underlined="false"
                            icon="tools/download-1"
                            icon-position="right"
                            class="ar-card__icon u-inline"
                            download
                            blank
                        >
                            {{ currentSlide.type === 'video' ? 'Перейти' : 'Скачать' }}
                        </z-link>
                    </z-button>

                    <div class="z-modal__navigation" v-if="slides && slides.length > 1">
                        <button
                            @click="changeSlide('prev')"
                            :title="text.prev"
                            :class="{ 'is-disabled' : !isPrev('prev') }"
                            class="z-modal__arrow z-modal__arrow--prev"
                            role="button"
                            ref="nav-prev"
                        >
                            <z-icon
                                name="arrows/caret"
                                width="24"
                                height="24"
                                dir="left"
                            ></z-icon>
                        </button>

                        <span>{{ currentSlideIndex + 1 }} / {{ slides.length }}</span>

                        <button
                            @click="changeSlide('next')"
                            :title="text.next"
                            :class="{ 'is-disabled' : !isNext('next') }"
                            class="z-modal__arrow z-modal__arrow--next"
                            role="button"
                            ref="nav-next"
                        >
                            <z-icon
                                name="arrows/caret"
                                dir="right"
                                width="24"
                                height="24"
                            ></z-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup>
import { nextTick, onMounted, onUpdated, ref, onBeforeUnmount, computed } from 'vue'
import ModalHtml from './components/Html.vue'
import { useEmitter } from '@/composable/useEmmiter'
import { useGetter } from '@/composable/useGetter'
import { useHandlers } from '@/components/ZModal/composable/useHandlers'
import { useComputedValue } from '@/components/ZModal/composable/useComputedValue'
import { localize } from '@/utils/i18n'
require('hammerjs')

const props = defineProps({
    id: {
        type: String,
        required: true
    },
    data: Array,
    overlay: {
        type: String,
        default: 'rgba(0, 0, 0, 0.7)'
    },
    theme: String,
    download: {
        type: Boolean,
        default: false
    },
    wide: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['open', 'close', 'slide-change'])
const { device } = useGetter(['device'])
const emitter = useEmitter()

const modal = ref(null)
const content = ref(null)
const container = ref(null)
const active = ref(false)
const currentSlide = ref(undefined)
const offsetTop = ref(0)
const offsetBottom = ref(0)

const slides = computed(() => props.data?.map((e, index) => ({ ...e, index })))

const text = {
    next: localize({
        ru: 'Следующий',
        en: 'Next',
        cn: 'Next'
    }),
    close: localize({
        ru: 'Закрыть',
        en: 'Close',
        cn: 'Close'
    }),
    prev: localize({
        ru: 'Предыдущий',
        en: 'Previous',
        cn: 'Previous'
    })
}

const {classObject, currentComponent, hasSlotData} = useComputedValue(props.theme, slides.value, currentSlide)

const {
    currentSlideIndex,
    openModal,
    closeModal,
    setOffsetValue,
    changeSlide,
    appearAnimationBefore,
    appearAnimationLeave,
    appearAnimationEnter,
    isPrev,
    isNext
} = useHandlers(props.id, active, modal, slides.value, currentSlide, content, container, offsetTop, offsetBottom, emit)

onMounted(async () => {
    emitter.on('open-modal', data => openModal(data))
    emitter.on('close-modal', data => closeModal(data))

    if (slides.value && slides.value.length) {
        currentSlide.value = slides.value[0]
    }

    document.addEventListener('keydown', event => {
        if (event.keyCode === 27 && active.value) {
            closeModal({id: props.id})
        }

        if (event.keyCode === 37 && active.value) {
            changeSlide('prev')
        }

        if (event.keyCode === 39 && active.value) {
            changeSlide('next')
        }
    }, false)

    await nextTick(() => {
        if (['mobile', 'tablet'].includes(device.value)) {
            if (!modal.value) return
            // eslint-disable-next-line
            var mc = new Hammer(modal.value)

            mc.on('swipe', function (ev) {
                if (ev.direction === 4) {
                    changeSlide('prev')
                } else if (ev.direction === 2) {
                    changeSlide('next')
                }
            })
        }
    })

    await nextTick(() => setOffsetValue())
    window.addEventListener('resize', setOffsetValue)
})

onBeforeUnmount(() => {
    emitter.off('open-modal', data => openModal(data))
    emitter.off('close-modal', data => openModal(data))

    window.removeEventListener('resize', setOffsetValue)
})

onUpdated(async () => {
    await nextTick(() => setOffsetValue())
})
</script>

<style lang="scss" src="./index.scss"></style>
